import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';

import { ToastrService } from 'ngx-toastr';
import { catchError, of, tap } from 'rxjs';

import { ENTRYPOINT } from '../../core/enpoint';
import { Status } from '../../shared/types';

export const LOGIN_URL = `${ENTRYPOINT}/api/token/`;
export const REFRESH_URL = `${ENTRYPOINT}/api/token/refresh/`;
export const LOGOUT_URL = `${ENTRYPOINT}/api/token/blacklist/`;
export interface LoginRes {
  refresh: string;
  access: string;
}

@Injectable({
  providedIn: 'root',
})
export class ProfileService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private toastr = inject(ToastrService);

  private accessTokenSignal = signal<string | null>(null);
  private loginStatusSignal = signal<Status>('idle');

  //selectors
  getAccessToken = computed(() => this.accessTokenSignal());
  isLoggedIn = computed(() => !!this.accessTokenSignal());
  loginStatus = computed(() => this.loginStatusSignal());

  postLogin(data: { username: string | null; password: string | null }) {
    this.loginStatusSignal.set('loading');
    return this.http
      .post<LoginRes>(LOGIN_URL, data, {
        withCredentials: true,
      })
      .pipe(
        tap((res) => {
          this.loginStatusSignal.set('success');
          this.setData(res);
          void this.router.navigate(['/create']);

          setTimeout(() => {
            this.loginStatusSignal.set('idle');
          }, 2000);
        }),
        catchError((error: unknown) => {
          this.loginStatusSignal.set('error');
          if (error instanceof HttpErrorResponse) {
            this.handleLoginError(error);
          }
          setTimeout(() => {
            this.loginStatusSignal.set('idle');
          }, 2000);
          return of(null);
        }),
      );
  }

  refreshToken(silent = true) {
    return this.http
      .post<{
        refresh: string;
        access: string;
      }>(REFRESH_URL, {}, { withCredentials: true })
      .pipe(
        catchError(() => {
          if (!silent) {
            this.logout();
            this.toastr.warning('Session has ended');
          }
          return of(null);
        }),
        tap((res) => {
          if (res) {
            this.setData(res);
          }
        }),
      );
  }

  private handleLoginError(error: HttpErrorResponse): void {
    if (error.status === 401 || error.status === 404) {
      this.toastr.error('User was not found');
    } else {
      this.toastr.error('Problem during login');
    }
  }
  setData(tokens: Partial<LoginRes>) {
    if (tokens.access) {
      this.accessTokenSignal.set(tokens.access);
    }
  }

  logout() {
    this.accessTokenSignal.set(null);
    void this.router.navigate(['/login']);
  }
  //blacklist the token as well, so no one can use it
  logoutBlacklist() {
    return this.http.post(LOGOUT_URL, {}, { withCredentials: true }).pipe(
      catchError(() => of(null)),
      tap(() => {
        this.logout();
      }),
    );
  }
}
